import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import React from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { AttachmentIcon, CommentsIcon } from '@/components/icons'
import { Tooltip } from '@/components/ui/Tooltip'
import { calculateBusinessDays, formatDateTime } from '@/utils/formatters'

import { resolveActionIcon } from './legacyActionIcons'
import { configureActionItemIcon } from './utils/renderHelpers'

export const DEFAULT_FILTERS = {
  actionNeeded: [],
  advisor: [],
  technician: [],
  agesOut: false,
  noUpdate: false,
  highDollar: false,
  new: false,
  isOpen: false,
  isClosed: false,
  reopened: false,
}

export const CHECKBOX_FILTERS = [
  'agesOut',
  'noUpdate',
  'highDollar',
  'new',
  'isOpen',
  'isClosed',
  'reopened',
]

export const CHECKBOX_FILTERS_WITH_DATES = {
  agesOut: `ages_out__lte=${dayjs().add(7, 'day').format('YYYY-MM-DD')}`,
  noUpdate: `last_updated_on__lte=${dayjs().subtract(5, 'day').format('YYYY-MM-DD')}`,
  highDollar: 'is_high_dollar=true',
  new: `created_at__gte=${dayjs().subtract(7, 'day').format('YYYY-MM-DD')}`,
  isOpen: 'is_open=true',
  isClosed: 'is_open=false',
  reopened: 'reopened=true',
}

// Date-range filters for the Resolved ROs section. Keys are the backend query
// params (date-only, inclusive on both ends — matches Global RO's pattern).
export const DEFAULT_RESOLVED_DATE_FILTERS = {
  resolved_at__gte: '',
  resolved_at__lte: '',
}

export const RESOLVED_DATE_RANGES = [
  { label: 'Resolved', from: 'resolved_at__gte', to: 'resolved_at__lte' },
]

export const COLUMNS = [
  {
    name: 'RO #',
    selector: (row) => row.roNumber,
    cell: (row) => (
      <Link
        to={`/dealer/${row.dealer}/repair-order/${row.id}`}
        className="hover:text-blue-dark flex w-full flex-row items-center gap-2 text-blue-800"
      >
        <span className="text-md truncate">{row.roNumber}</span>
        <p className="sr-only">Repair Order is {row.isOpen ? 'open' : 'not open'}</p>
        <div
          className={`h-[13px] w-[13px] shrink-0 rounded-full border-[3px] ${
            row.isOpen ? 'border-green-800' : 'border-charcoal-400 bg-charcoal-400'
          }`}
        />
      </Link>
    ),
    sortable: true,
    sortBy: 'ro_number',
    width: '150px',
  },
  {
    name: 'Amount',
    selector: (row) => row.totalUnresolvedClaimAmount,
    cell: (row) => (
      <span>
        {Number(row.totalUnresolvedClaimAmount).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })}
      </span>
    ),
    sortable: true,
    sortBy: 'total_unresolved_claim_amount',
    width: '120px',
  },
  {
    name: 'ADV ID',
    selector: (row) => row.advisor,
    sortable: true,
    sortBy: 'advisor',
    width: '180px',
  },
  {
    name: 'Days On List',
    selector: (row) => row.daysOnList,
    format: (row) => {
      if (row.daysOnList) {
        return (
          <span>
            {row.daysOnList} day{(row.daysOnList > 1 || row.daysOnList === 0) && 's'}
          </span>
        )
      }
      return <span>N/A</span>
    },
    sortable: true,
    sortBy: 'days_on_list',
    width: '140px',
  },
  {
    name: 'Ages Out',
    selector: (row) => row.agesOut,
    format: (row) => {
      // Calculate when the repair order will age out (excluding the time)
      const agesOut = dayjs(row.agesOut).startOf('day').diff(dayjs().startOf('day'), 'day')
      return (
        <span className={mergeClassNames(agesOut < 7 && 'text-red font-semibold')}>
          {agesOut} day{(agesOut > 1 || agesOut === 0) && 's'}
        </span>
      )
    },
    sortable: true,
    sortBy: 'ages_out',
    width: '120px',
  },
  {
    name: 'Action Needed',
    cell: (row) => {
      // Action name, icon, and completed status are nested pretty far deep under `lineItems`
      // We need to go through every lineitem's actions needed to get them
      const actions = _.map(
        _.flatMap(row.lineItems, (l) => _.map(l.actionsNeeded, (a) => a)),
        (a) => ({
          name: a.action.name,
          icon: resolveActionIcon(a.action),
          completed: a.completedAt,
        }),
      )

      // For each action type, check if every action item has been completed
      const groupedActions = _.groupBy(actions, 'name')
      const actionIcons = _.orderBy(
        _.map(groupedActions, (a, name) => ({
          // Every action in a group is the same action, so they share an icon
          icon: a[0].icon,
          completed: _.every(a, 'completed'),
          name,
        })),
        'completed',
        'desc',
      )

      return (
        <div className="flex flex-row items-center gap-1">
          {_.map(actionIcons, (a) => (
            <Tooltip
              placement="top"
              key={a.name}
              content={
                <div className="text-md text-white-dark rounded-lg border border-blue-800 bg-blue-800 p-1.5 shadow-md">
                  <span>{a.name}</span>
                </div>
              }
            >
              {configureActionItemIcon(a.icon, a.completed)}
              <span className="sr-only">{a.name}</span>
            </Tooltip>
          ))}
        </div>
      )
    },
    width: '250px',
  },
  {
    name: 'Last Update',
    selector: (row) => row.lastUpdatedOn,
    format: (row) => {
      const businessDaysDiff = calculateBusinessDays(row.lastUpdatedOn, new Date())

      return (
        <span className={mergeClassNames(businessDaysDiff >= 2 && 'text-red font-semibold')}>
          {formatDateTime(row.lastUpdatedOn)}
        </span>
      )
    },
    sortable: true,
    sortBy: 'last_updated_on',
    width: '200px',
  },
  {
    name: 'Tech #',
    selector: (row) => row.lineItems,
    cell: (row) => (
      <span>{_.compact(_.uniq(_.map(row.lineItems, (l) => l.technician))).join(', ')}</span>
    ),
    sortable: false,
    width: '200px',
  },
  {
    name: '',
    selector: (row) => row.id,
    cell: (row) => (
      <div className="flex flex-row gap-3">
        <button type="button">
          <AttachmentIcon
            className={mergeClassNames(
              'h-6',
              row.hasUnreadAttachments ? 'stroke-blue-800' : 'stroke-charcoal-light',
            )}
            unread={row.hasUnreadAttachments}
          />
        </button>

        <button type="button">
          <CommentsIcon
            className={mergeClassNames(
              'h-6',
              row.hasUnreadComments ? 'fill-blue-800' : 'fill-charcoal-light',
            )}
            unread={row.hasUnreadComments}
          />
        </button>
      </div>
    ),
    width: '90px',
  },
]

export const RESOLVED_COLUMNS = [
  {
    name: 'RO #',
    selector: (row) => row.roNumber,
    cell: (row) => (
      <Link
        to={`/dealer/${row.dealer}/repair-order/${row.id}`}
        className="hover:text-blue-dark flex flex-row items-center gap-2 text-blue-800"
      >
        <span className="text-md">{row.roNumber}</span>
        <p className="sr-only">Repair Order is {row.isOpen ? 'open' : 'not open'}</p>
        {row.isOpen ? (
          <div className="h-[13px] w-[13px] rounded-full border-[3px] border-green-800" />
        ) : (
          <div className="border-charcoal-400 bg-charcoal-400 h-[13px] w-[13px] rounded-full border-[3px]" />
        )}
      </Link>
    ),
    sortable: true,
    sortBy: 'ro_number',
    width: '150px',
  },
  {
    name: 'Amount',
    selector: (row) => row.totalClaimAmount,
    cell: (row) => (
      <span>
        {Number(row.totalClaimAmount).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })}
      </span>
    ),
    sortable: true,
    sortBy: 'total_claim_amount',
    width: '120px',
  },
  {
    name: 'ADV ID',
    selector: (row) => row.advisor,
    sortable: true,
    sortBy: 'advisor',
    width: '180px',
  },
  {
    name: '# Lines Resolved',
    selector: (row) => row.numLineItemsResolved,
    sortable: true,
    sortBy: 'num_line_items_resolved',
    width: '150px',
  },
  {
    name: '# Written Off',
    selector: (row) => row.numLineItemsWrittenOff,
    sortable: true,
    sortBy: 'num_line_items_written_off',
    width: '150px',
  },
  {
    name: 'Tech #',
    cell: (row) => (
      <div className="flex flex-row items-center gap-1">
        {_.compact(_.uniq(_.map(row.lineItems, (l) => l.technician))).join(', ')}
      </div>
    ),
    width: '180px',
  },
  {
    name: 'Days On List',
    selector: (row) => row.daysOnList,
    format: (row) => {
      if (row.daysOnList) {
        return (
          <span>
            {row.daysOnList} day{(row.daysOnList > 1 || row.daysOnList === 0) && 's'}
          </span>
        )
      }
      return <span>N/A</span>
    },
    sortable: true,
    sortBy: 'days_on_list',
    width: '140px',
  },
  {
    name: 'Resolved Date',
    selector: (row) => row.resolvedAt,
    format: (row) => {
      if (row.resolvedAt) {
        return <span>{dayjs(row.resolvedAt).format('MM/DD/YYYY')}</span>
      }
      // Return default message if does not have resolved date
      return 'N/A'
    },
    sortable: true,
    sortBy: 'resolved_at',
    width: '150px',
  },
]
